import { create } from 'zustand';
import type { WeatherStation } from '../types/weatherStation';
import { createLogger } from '../utils/logger';
import { fetchJSON } from '../utils/fetchClient';
import { weatherStationAPI, APIError, ErrorType } from '../services/WeatherStationAPI';

const log = createLogger('FilterStore');

// Filter state interface
export interface FilterState {
  selectedCountries: Set<string>;
  selectedStates: Map<string, Set<string>>; // country -> states
  selectedCities: Map<string, Map<string, Set<string>>>; // country -> state -> cities
  isFilterPanelOpen: boolean;
  searchQuery: string;
}

// Country/State data interface
export interface CountryStateData {
  countries: string[]; // Sorted list of unique countries
  statesByCountry: Map<string, string[]>; // country -> sorted states
}

// Boundary data interface for background loading
export interface BoundaryData {
  type: 'country' | 'state' | 'city';
  name: string;
  geometry: any; // GeoJSON geometry
}

interface FilterStoreState {
  filterState: FilterState;
  countryStateData: CountryStateData;
  filteredStations: WeatherStation[];
  filterError: string | null;
  isLoadingCountryData: boolean;
  boundariesLoading: boolean;
  boundaries: BoundaryData[];
}

interface FilterStoreActions {
  applyFilter: (countries: Set<string>, states: Map<string, Set<string>>, cities: Map<string, Map<string, Set<string>>>) => Promise<void>;
  loadCountryStateData: () => Promise<void>;
  clearFilter: () => void;
  toggleFilterPanel: () => void;
  loadBoundariesAsync: (countries: Set<string>, states: Map<string, Set<string>>, cities: Map<string, Map<string, Set<string>>>) => Promise<void>;
}

export type FilterStore = FilterStoreState & FilterStoreActions;

// ponytail: callbacks for cross-store error/warning display.
// Set externally by app init so filterStore stays decoupled.
let onError: ((message: string, actionLabel?: string, onAction?: () => void) => void) | undefined;
let onWarning: ((message: string) => void) | undefined;

export function setFilterErrorHandler(handler: typeof onError) { onError = handler; }
export function setFilterWarningHandler(handler: typeof onWarning) { onWarning = handler; }

export const useFilterStore = create<FilterStore>((set, get) => ({
  // --- State (defaults match appStore initialState) ---
  filterState: {
    selectedCountries: new Set<string>(),
    selectedStates: new Map<string, Set<string>>(),
    selectedCities: new Map<string, Map<string, Set<string>>>(),
    isFilterPanelOpen: false,
    searchQuery: '',
  },
  countryStateData: {
    countries: [],
    statesByCountry: new Map<string, string[]>(),
  },
  filteredStations: [],
  filterError: null,
  isLoadingCountryData: false,
  boundariesLoading: false,
  boundaries: [],

  // --- Actions ---

  loadCountryStateData: async () => {
    log.debug('📊 Loading country/state data...');

    set({ isLoadingCountryData: true, filterError: null });

    try {
      const api = weatherStationAPI;

      const stations = await api.getAllStationsForMap();
      log.debug(`✅ Loaded ${stations.length} stations for country/state extraction`);

      const countries = api.extractCountries(stations);
      log.debug(`📍 Found ${countries.length} unique countries`);

      const statesByCountry = new Map<string, string[]>();
      countries.forEach(country => {
        const states = api.extractStates(stations, country);
        if (states.length > 0) {
          statesByCountry.set(country, states);
          log.debug(`   ${country}: ${states.length} states`);
        }
      });

      set({
        countryStateData: { countries, statesByCountry },
        isLoadingCountryData: false,
        filterError: null,
      });

      log.info('✅ Country/state data loaded successfully');
    } catch (error) {
      log.error('❌ Failed to load country/state data:', error);

      let errorMessage = 'Failed to load country list. Please try again.';

      if (error instanceof APIError) {
        if (error.type === ErrorType.NETWORK_ERROR) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.type === ErrorType.SERVICE_UNAVAILABLE) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (error.type === ErrorType.AUTHENTICATION_FAILED) {
          errorMessage = 'Authentication failed. Please refresh the page.';
        }
      }

      set({ isLoadingCountryData: false, filterError: errorMessage });

      if (onError) {
        onError(errorMessage, 'Retry', () => { get().loadCountryStateData(); });
      }
    }
  },

  applyFilter: async (countries, states, cities) => {
    const useOptimizedFiltering = import.meta.env.VITE_USE_OPTIMIZED_FILTERING === 'true';

    log.debug(`🔍 [applyFilter] Using ${useOptimizedFiltering ? 'OPTIMIZED' : 'LEGACY'} filtering implementation`);

    const startTime = performance.now();
    log.debug('🔍 [applyFilter] Starting filter...', {
      countries: Array.from(countries),
      stateCount: Array.from(states.values()).reduce((n, s) => n + s.size, 0),
      cityCount: Array.from(cities.values()).reduce((n, sm) => Array.from(sm.values()).reduce((c, cs) => c + cs.size, n), 0),
    });

    set({ filterError: null });

    try {
      let filteredStations: WeatherStation[] = [];

      const hasStates = states.size > 0 && Array.from(states.values()).some(s => s.size > 0);
      const hasCities = cities.size > 0 && Array.from(cities.values()).some(sm =>
        Array.from(sm.values()).some(cs => cs.size > 0)
      );

      // Fast path: country-only search via obs wrapper (no spatial filtering needed)
      if (countries.size > 0 && !hasStates && !hasCities && !useOptimizedFiltering) {
        log.debug('⚡ [applyFilter] Fast path: country-only search via obs wrapper');
        const baseURL = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001';
        const countryList = Array.from(countries).join(',');

        const data = await fetchJSON<any[]>(`${baseURL}/api/weather/weatherstations?Country=${encodeURIComponent(countryList)}`, {
          signal: AbortSignal.timeout(120000),
        });

        if (data && Array.isArray(data)) {
          filteredStations = data
            .map((s: any) => {
              const geo = s.geoPoint || {};
              const lat = Number(geo.latitude ?? s.latitude ?? NaN);
              const lon = Number(geo.longitude ?? s.longitude ?? NaN);
              if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
              const code = s.stationCode || s.station_code || '';
              if (!code) return null;
              return {
                stationCode: code,
                latitude: lat,
                longitude: lon,
                elevation: s.elevation || 0,
                country: s.country || '',
                state: s.state || '',
                tags: typeof s.tags === 'string' ? JSON.parse(s.tags) : (s.tags || {}),
                firstObsTimestamp: s.firstObsTimestamp || s.first_obs_timestamp || '',
                lastObsTimestamp: s.lastObsTimestamp || s.last_obs_timestamp || '',
                startTime: s.startTime || s.start_time,
                devices: Array.isArray(s.devices) ? s.devices.map((d: any) => ({
                  stationId: d.stationId || d.station_id || '',
                  sourceStationId: d.sourceStationId || d.source_station_id || undefined,
                  dataSourceId: d.dataSourceId || d.data_source_id || '',
                  type: d.type || d.network_type || '',
                })) : [],
                parameters: Array.isArray(s.parameters) ? s.parameters : [],
                isActive: s.active === true || s.active === 'true',
              } as WeatherStation;
            })
            .filter((s: WeatherStation | null): s is WeatherStation => s !== null);

          const duration = performance.now() - startTime;
          log.debug(`✅ [applyFilter] Fast path complete: ${filteredStations.length} stations (${duration.toFixed(0)}ms)`);
        }
      } else {
        if (useOptimizedFiltering) {
          log.debug('⚡ [applyFilter] Using OptimizedFilteringService for fast field matching');

          const { OptimizedFilteringService } = await import('../services/OptimizedFilteringService');
          const optimizedService = new OptimizedFilteringService();

          const filterStart = performance.now();
          const result = await optimizedService.filterMultiRegion({
            countries,
            states,
            cities,
            validateQuality: true,
          });
          const filterDuration = performance.now() - filterStart;

          log.debug(`✅ [applyFilter] Optimized filter complete: ${result.stations.length} stations, ${result.metadata.matchRate.toFixed(1)}% match in ${(performance.now() - startTime).toFixed(0)}ms`);

          log.debug(`📈 [applyFilter] Data quality: ${result.metadata.dataQuality.qualityScore.toFixed(1)}%`);

          // PERFORMANCE FIX: Disable spatial filtering fallback - too slow
          const shouldFallbackToSpatial = false;

          if (shouldFallbackToSpatial) {
            log.warn(`⚠️ [applyFilter] Data quality score below threshold, falling back to spatial`);
            filteredStations = await runSpatialFallback(countries, states, cities);
          } else {
            // Show warning if quality < 90%
            if (result.metadata.dataQuality.qualityScore < 90 && onWarning) {
              const missingFields: string[] = [];
              if (result.metadata.dataQuality.missingCountry > 0) {
                missingFields.push(`${result.metadata.dataQuality.missingCountry} stations missing country`);
              }
              if (result.metadata.dataQuality.missingState > 0) {
                missingFields.push(`${result.metadata.dataQuality.missingState} stations missing state`);
              }
              if (result.metadata.dataQuality.missingCity > 0) {
                missingFields.push(`${result.metadata.dataQuality.missingCity} stations missing city`);
              }
              const warningMessage = `Data quality: ${result.metadata.dataQuality.qualityScore.toFixed(0)}%. ${missingFields.join(', ')}. Some stations may be missing from search results.`;
              onWarning(warningMessage);
              log.warn(`⚠️ [applyFilter] ${warningMessage}`);
            }
            filteredStations = result.stations;
          }
        } else {
          // Legacy spatial filtering when flag is disabled
          log.debug('🗺️ [applyFilter] Using SpatialFilteringService for boundary-based filtering');
          filteredStations = await runSpatialFallback(countries, states, cities);

          const totalDuration = performance.now() - startTime;
          log.debug(`✅ [applyFilter] Legacy spatial filter complete:`, {
            totalTime: `${totalDuration.toFixed(0)}ms`,
            stationsFound: filteredStations.length,
          });
        }
      } // close fast-path else

      // Handle empty results
      if (filteredStations.length === 0) {
        const locationName = countries.size > 0 ? Array.from(countries)[0] : 'selected region';
        const errorMessage = `No weather stations found in ${locationName}`;
        log.warn(`⚠️ ${errorMessage}`);
        if (onError) { onError(errorMessage); }
      }

      set({
        filteredStations,
        filterState: {
          ...get().filterState,
          selectedCountries: new Set(countries),
          selectedStates: new Map(states),
          selectedCities: new Map(cities),
          isFilterPanelOpen: false,
        },
        filterError: null,
      });

      log.debug(`[applyFilter] Found ${filteredStations.length} stations in selected region(s), fitting bounds`);

      // Dispatch custom event to trigger fitBounds in MapView
      window.dispatchEvent(new CustomEvent('fitStationBounds', {
        detail: { stations: filteredStations, padding: 50 },
      }));

      // Load boundaries in background for optimized filtering
      if (useOptimizedFiltering) {
        get().loadBoundariesAsync(countries, states, cities);
      }
    } catch (error) {
      log.error('❌ Failed to apply filter:', error);

      let errorMessage = 'Failed to apply filter. Please try again.';

      if (error instanceof APIError) {
        if (error.type === ErrorType.NETWORK_ERROR) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.type === ErrorType.SERVICE_UNAVAILABLE) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (error.type === ErrorType.AUTHENTICATION_FAILED) {
          errorMessage = 'Authentication failed. Please refresh the page.';
        }
      }

      set({ filterError: errorMessage });

      if (onError) {
        onError(errorMessage, 'Retry', () => { get().applyFilter(countries, states, cities); });
      }
    }
  },

  loadBoundariesAsync: async (countries, states, cities) => {
    log.debug('🗺️ [loadBoundariesAsync] Starting background boundary loading...');
    set({ boundariesLoading: true });

    try {
      const { GADMLocationService } = await import('../services/GADMLocationService');
      const gadmService = new GADMLocationService();
      await gadmService.initialize();

      const boundaries: BoundaryData[] = [];

      // Load country boundaries
      if (countries.size > 0) {
        log.debug(`  Loading ${countries.size} country boundaries...`);
        for (const country of countries) {
          try {
            const boundary = await gadmService.getCountryBoundary(country);
            if (boundary) {
              boundaries.push({ type: 'country', name: country, geometry: boundary });
              log.debug(`    ✓ Loaded boundary for ${country}`);
            }
          } catch (error) {
            log.warn(`    ⚠️ Failed to load boundary for ${country}:`, error);
          }
        }
      }

      // Load state boundaries
      if (states.size > 0) {
        for (const [country, stateSet] of states) {
          for (const state of stateSet) {
            try {
              const boundary = await gadmService.getStateBoundary(country, state);
              if (boundary) {
                boundaries.push({ type: 'state', name: `${state}, ${country}`, geometry: boundary });
                log.debug(`    ✓ Loaded boundary for ${state}, ${country}`);
              }
            } catch (error) {
              log.warn(`    ⚠️ Failed to load boundary for ${state}, ${country}:`, error);
            }
          }
        }
      }

      // Load city boundaries
      if (cities.size > 0) {
        for (const [country, stateMap] of cities) {
          for (const [state, citySet] of stateMap) {
            for (const city of citySet) {
              try {
                const boundary = await gadmService.getCityBoundary(country, state, city);
                if (boundary) {
                  boundaries.push({ type: 'city', name: `${city}, ${state}, ${country}`, geometry: boundary });
                  log.debug(`    ✓ Loaded boundary for ${city}, ${state}, ${country}`);
                }
              } catch (error) {
                log.warn(`    ⚠️ Failed to load boundary for ${city}, ${state}, ${country}:`, error);
              }
            }
          }
        }
      }

      set({ boundaries, boundariesLoading: false });
      log.debug(`✅ [loadBoundariesAsync] Loaded ${boundaries.length} boundaries in background`);
    } catch (error) {
      log.error('❌ [loadBoundariesAsync] Failed to load boundaries:', error);
      set({ boundariesLoading: false });
    }
  },

  clearFilter: () => {
    log.debug('🧹 Clearing filters...');
    set({
      filteredStations: [],
      filterState: {
        selectedCountries: new Set<string>(),
        selectedStates: new Map<string, Set<string>>(),
        selectedCities: new Map<string, Map<string, Set<string>>>(),
        isFilterPanelOpen: false,
        searchQuery: '',
      },
      filterError: null,
    });
    log.debug('✅ Filters cleared');
  },

  toggleFilterPanel: () => {
    const currentState = get().filterState.isFilterPanelOpen;
    log.debug(`🔄 Toggling filter panel: ${currentState} -> ${!currentState}`);
    set({
      filterState: {
        ...get().filterState,
        isFilterPanelOpen: !currentState,
      },
    });
  },
}));

// TODO: Cross-store communication — when stationDataStore.allStations changes,
// filterStore may need to re-derive countryStateData or re-apply the active filter.
// Wire via stationDataStore.subscribe() once stationDataStore is extracted.

// --- Internal helper: spatial filtering fallback (shared by both branches) ---
async function runSpatialFallback(
  countries: Set<string>,
  states: Map<string, Set<string>>,
  cities: Map<string, Map<string, Set<string>>>
): Promise<WeatherStation[]> {
  const { SpatialFilteringService } = await import('../services/SpatialFilteringService');
  const { GADMLocationService } = await import('../services/GADMLocationService');

  const spatialService = new SpatialFilteringService();
  const gadmService = new GADMLocationService();
  await gadmService.initialize();

  const stationMap = new Map<string, WeatherStation>();

  // ponytail: single converter used by all three loops
  function addStations(rawStations: any[]) {
    for (const s of rawStations) {
      if (stationMap.has(s.stationCode)) continue;
      stationMap.set(s.stationCode, {
        stationCode: s.stationCode,
        latitude: s.geoPoint.latitude,
        longitude: s.geoPoint.longitude,
        elevation: s.elevation,
        tags: s.tags,
        firstObsTimestamp: s.firstObsTimestamp,
        lastObsTimestamp: s.lastObsTimestamp,
        startTime: s.startTime,
        devices: s.devices,
        parameters: (s as any).parameters || [],
        country: s.country,
        state: s.state,
        isActive: s.active,
      });
    }
  }

  for (const country of countries) {
    try {
      const boundary = await gadmService.getCountryBoundary(country);
      if (boundary) addStations(await spatialService.getStationsForCountry(country, boundary));
    } catch (error) { log.warn(`Failed to load boundary for ${country}:`, error); }
  }

  for (const [country, stateSet] of states) {
    for (const state of stateSet) {
      try {
        const boundary = await gadmService.getStateBoundary(country, state);
        if (boundary) addStations(await spatialService.getStationsForState(country, state, boundary));
      } catch (error) { log.warn(`Failed to load boundary for ${state}, ${country}:`, error); }
    }
  }

  for (const [country, stateMap] of cities) {
    for (const [state, citySet] of stateMap) {
      for (const city of citySet) {
        try {
          const boundary = await gadmService.getCityBoundary(country, state, city);
          if (boundary) addStations(await spatialService.getStationsForCity(country, state, city, boundary));
        } catch (error) { log.warn(`Failed to load boundary for ${city}, ${state}, ${country}:`, error); }
      }
    }
  }

  return Array.from(stationMap.values());
}
